import {
  Button,
  Card,
  Checkbox,
  Col,
  Input,
  Row,
  Select,
  Skeleton,
  Table,
  Tooltip,
} from "antd";
import { useContext, useEffect, useState } from "react";
import { useTheme } from "../../Context/ThemeContext";
import { useClient } from "../../Context/ClientContext";
import { ColumnsType } from "antd/es/table";
import {
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import {
  LedgerStatus,
  LedgerStatusDropDown,
} from "../../Services/LedgerService";
import { notificationContext } from "../../Common/Routing/PageRoute";
import OrganisationsService, {
  Department_Type,
  DepartmentTypeList,
  IDepttData,
} from "../../Services/Organisations/OrganisationsService";
import AddDepttModel from "./AddDepttModel";
import { OrgDataType } from "./Organisations";
import { IApiResponse } from "../../../Utilities/ApiUtility.axios";
import { IIdNameModel } from "../../Services/ItemsService";
import { ConfirmationModal } from "../../Common/ConfirmationModal";
import { IDropdownProps } from "../Settings/VoucherTemplate/SettingVoucherTemplate";
import { tablePageSizeOptions } from "../../Common/ThemeSetting";

interface DepttDataType extends OrgDataType {
  depttName: string;
  depttType: Department_Type;
  subUnitName: string;
}

const Departments = () => {
  const { currentClient } = useClient();
  const [addUpdateTrigger, setAddUpdateTrigger] = useState<boolean>(false);
  const [searchDeptt, setSearchDeptt] = useState<string>("");
  const { themeData } = useTheme();
  const { openNotification } = useContext(notificationContext);
  const [openModel, setOpenModel] = useState<{ type: string; param: any }>({
    type: "",
    param: {},
  });
  const [trigger, setTrigger] = useState<boolean>(false);
  const [depttList, setDepttList] = useState<DepttDataType[]>([]);
  const [dataLoading, setDataLoading] = useState<boolean>(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(
    themeData?.tablePageSize ?? 15
  );
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [filter, setfilter] = useState<any>({
    sortCol: "",
    sortDir: "",
  });
  const [ledgerStatus, setLedgerStatus] = useState<LedgerStatus>(
    LedgerStatus.active
  );
  // const [exportType, setExportType] = useState<number>(-1);
  // const [exportDepData, setExportDepData] = useState<any[]>([]);

  const getDepttList = async () => {
    setDataLoading(true);
    await OrganisationsService.getDepttList(
      currentClient?.id!,
      (page - 1) * pageSize,
      pageSize,
      searchDeptt,
      filter.sortCol,
      filter.sortDir,
      ledgerStatus
    )
      .then((val) => {
        if (val?.items) {
          setDepttList(
            val.items?.map<DepttDataType>(
              (deptt: IDepttData, index: number) => {
                return {
                  sno: deptt.sno,
                  id: deptt.id as string,
                  depttName: deptt.depttName,
                  depttType: deptt.depttType,
                  orgName: deptt.orgName as string,
                  subUnitName: deptt.subUnitName as string,
                  status: deptt.status!,
                };
              }
            ) || []
          );
          setTotalRecords(val?.totalRecords);
        }
      })
      .catch((ex: any) => {
        console.error(ex.message);
        setDataLoading(false);
        setDepttList([]);
      });
    setDataLoading(false);
  };

  const handleDelete = async (selId: string) => {
    if (selId) {
      await OrganisationsService.deleteDepartment(currentClient?.id!, selId)
        .then((val: IApiResponse<IIdNameModel>) => {
          if (val?.status) {
            openNotification(
              "success",
              val?.result?.name + " department deleted successfully"
            );
            setAddUpdateTrigger((prev) => !prev);
          } else {
            openNotification("error", val?.errors && val?.errors[0]?.message);
          }
        })
        .catch((err) => {
          openNotification("error", err?.message || "");
        });
      setOpenModel({
        type: "",
        param: {},
      });
    }
  };

  useEffect(() => {
    if (currentClient?.id) {
      getDepttList();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentClient?.id,
    addUpdateTrigger,
    searchDeptt,
    ledgerStatus,
    trigger,
    filter,
    page,
    pageSize,
  ]);

  // --------- Departments Table Columns  ---------
  const depttCols: ColumnsType<DepttDataType> = [
    {
      title: "S.No.",
      dataIndex: "sno",
      key: "sno",
      width: "5%",
      render: (value: number) => (
        <Skeleton
          active
          loading={dataLoading}
          paragraph={{ rows: 1 }}
          title={false}
        >
          {value}
        </Skeleton>
      ),
    },
    {
      title: "Department's Name",
      dataIndex: "depttName",
      sorter: true,
      defaultSortOrder: "ascend",
      render: (value: string) => (
        <Skeleton
          active
          loading={dataLoading}
          paragraph={{ rows: 1 }}
          title={false}
        >
          {value}
        </Skeleton>
      ),
    },
    {
      title: "Department's Type",
      dataIndex: "depttType",
      render: (value: Department_Type) => (
        <Skeleton
          active
          loading={dataLoading}
          paragraph={{ rows: 1 }}
          title={false}
        >
          {
            DepartmentTypeList.find(
              (deptt: IDropdownProps) => deptt.value === value
            )?.label
          }
        </Skeleton>
      ),
    },
    {
      title: "Sub Unit Name",
      dataIndex: "subUnitName",
      sorter: true,
      render: (value: string) => (
        <Skeleton
          active
          loading={dataLoading}
          paragraph={{ rows: 1 }}
          title={false}
        >
          {value}
        </Skeleton>
      ),
    },
    {
      title: "Organisation Name",
      dataIndex: "orgName",
      sorter: true,
      render: (value: string) => (
        <Skeleton
          active
          loading={dataLoading}
          paragraph={{ rows: 1 }}
          title={false}
        >
          {value}
        </Skeleton>
      ),
    },
    {
      title: "Action",
      dataIndex: "id",
      width: "10%",
      render: (_, record) => (
        <Skeleton
          active
          loading={dataLoading}
          paragraph={{ rows: 1 }}
          title={false}
        >
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <div>
              {record.status !== LedgerStatus.deleted && (
                <Tooltip
                  title={
                    record?.status === LedgerStatus.active
                      ? "In-Active"
                      : "Active"
                  }
                  placement="top"
                  color="#3D4998"
                >
                  <Checkbox
                    checked={record?.status !== LedgerStatus.deactive}
                    title="Delete"
                    onChange={(e: any) => {
                      // console.log(e.target.checked, record);
                      setOpenModel({
                        type: "isActive",
                        param: {
                          name: record.depttName,
                          id: record.id,
                          itemStatus: record?.status,
                        },
                      });
                      // setSelectChange(record?.isActive ? "In-Active" : "Active");
                    }}
                  />
                </Tooltip>
              )}
            </div>
            <div>
              {record.status === LedgerStatus.active ? (
                <Tooltip title="Edit" placement="top" color="#3D4998">
                  <EditOutlined
                    style={{
                      color: "#444653",
                      cursor: "pointer",
                    }}
                    onClick={() => {
                      setOpenModel({
                        type: "addDepartment",
                        param: {
                          id: record.id,
                        },
                      });
                    }}
                  />
                </Tooltip>
              ) : (
                <></>
              )}
            </div>
            <div>
              <Tooltip title="Delete" placement="top" color="red">
                <DeleteOutlined
                  style={{
                    color: "red",
                    cursor: "pointer",
                  }}
                  onClick={() =>
                    setOpenModel({
                      type: "delete",
                      param: {
                        id: record.id,
                        name: record.depttName,
                      },
                    })
                  }
                />
              </Tooltip>
            </div>
          </div>
        </Skeleton>
      ),
    },
  ];

  // let path = "Department list";
  // const columnsA = ["Department Name"];

  // const fieldMapping = {
  //   // "S.No.": (item: any, index: number) => index,
  //   "Department Name": (item: any, index: number) => item?.depttName,
  // };

  // const generateFooterRow = (columns: string[]) => {
  //   return columns.map((col) => {});
  // };

  // const exportCsv = (exportData: any[]) => {
  //   InvoiceExportService.generateCSV(
  //     exportData,
  //     columnsA,
  //     fieldMapping,
  //     generateFooterRow,
  //     "",
  //     "",
  //     path,
  //     currentClient?.businessName
  //   );
  //   setExportType(0);
  // };

  // const exportPDF = (exportData: any[]) => {
  //   InvoiceExportService.generatePDF(
  //     exportData,
  //     currentClient?.businessName,
  //     columnsA,
  //     fieldMapping,
  //     generateFooterRow,
  //     "",
  //     path
  //   );
  //   setExportType(0);
  // };

  // const exportExcel = async (exportData: any[]) => {
  //   await InvoiceExportService.generateExcel(
  //     exportData,
  //     currentClient?.businessName,
  //     columnsA,
  //     fieldMapping,
  //     generateFooterRow,
  //     "",
  //     path
  //   );
  //   setExportType(0);
  // };

  // const content = (
  //   <Space direction="vertical">
  //     <div>
  //       <Button
  //         style={{ width: "80px" }}
  //         size="small"
  //         title="CSV"
  //         onClick={() => {
  //           if (exportDepData.length > 0) {
  //             exportCsv(exportDepData);
  //           } else {
  //             setExportType(1);
  //             // setApiCallExports(true);
  //           }
  //         }}
  //         icon={<DownloadOutlined />}
  //       >
  //         CSV
  //       </Button>
  //     </div>

  //     <div>
  //       <Button
  //         style={{ width: "80px" }}
  //         size="small"
  //         title="PDF"
  //         onClick={() => {
  //           if (exportDepData.length > 0) {
  //             exportPDF(exportDepData);
  //           } else {
  //             setExportType(2);
  //             // setApiCallExports(true);
  //           }
  //         }}
  //         icon={<DownloadOutlined />}
  //       >
  //         PDF
  //       </Button>
  //     </div>
  //     <div>
  //       <Button
  //         style={{ width: "80px" }}
  //         size="small"
  //         title="XLSX"
  //         onClick={() => {
  //           if (exportDepData.length > 0) {
  //             exportExcel(exportDepData);
  //           } else {
  //             setExportType(3);
  //             // setApiCallExports(true);
  //           }
  //         }}
  //         icon={<DownloadOutlined />}
  //       >
  //         XLSX
  //       </Button>
  //     </div>
  //   </Space>
  // );

  //----- Table Ascending & Desending Order -----
  const tableSort = (pagination?: any, sort?: any, filter?: any) => {
    setfilter({
      sortCol: filter.column !== undefined ? filter.columnKey : "name",
      sortDir:
        filter.column !== undefined
          ? filter.order === "descend"
            ? "desc"
            : "asc"
          : "desc",
    });
    // console.log("sort1111111111111", sort?.TaxRate?.Rate, sort);
  };

  return (
    <Card
      size="small"
      className="TableCard"
      style={{
        marginTop: 35,
        boxShadow: "0px 0px 10px 0px #96A1C84D",
      }}
    >
      <Row justify="space-between">
        <Col>
          <Row gutter={15}>
            <Col>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() =>
                  setOpenModel({
                    type: "addDepartment",
                    param: {},
                  })
                }
              >
                Department
              </Button>
            </Col>
            {/* <Col>
                {depttList.length === 0 ? (
                  <Button icon={<DownloadOutlined />} disabled type="primary">
                    Export
                  </Button>
                ) : (
                  <Popover
                    content={content}
                    placement="rightTop"
                    trigger="hover"
                  >
                    <Button
                      type="default"
                      icon={<DownloadOutlined />}
                      // onClick={() => exportCsv()}
                    >
                      Export
                    </Button>
                  </Popover>
                )}
              </Col> */}
          </Row>
        </Col>
        <Col>
          <Row gutter={8}>
            <Col>
              <Input
                allowClear
                style={{ width: "220px" }}
                placeholder="Search.."
                onChange={(e: any) => {
                  setDataLoading(true);
                  if (!e.cancelable) {
                    //console.log("typeing user............");
                    const searchItemGroup = setTimeout(() => {
                      setSearchDeptt(e.target.value);
                    }, 1000);
                    // setExportDepData([]);
                    // setExportType(-1);
                    return () => clearTimeout(searchItemGroup);
                  } else {
                    // console.log("Click cancle............");
                    setSearchDeptt(e.target.value);
                  }
                }}
                suffix={dataLoading || searchDeptt ? "" : <SearchOutlined />}
              />
            </Col>
            <Col>
              <Select
                style={{ width: "110px" }}
                defaultValue={LedgerStatus.active}
                onSelect={(e) => {
                  setLedgerStatus(e);
                  // setExportItemsData([]);
                  setPage(1);
                }}
                options={LedgerStatusDropDown}
              />
            </Col>
          </Row>
        </Col>
      </Row>
      <Table
        className={`Tabel-style table-${themeData?.componentSize ?? "middle"}`}
        columns={depttCols}
        dataSource={
          dataLoading
            ? (Array.from({ length: pageSize }, (_, index) => ({
                key: `loading-${index}`,
              })) as DepttDataType[])
            : depttList
        }
        locale={{
          triggerDesc: "",
          triggerAsc: "",
          cancelSort: "",
        }}
        onChange={tableSort}
        pagination={{
          total: totalRecords,
          current: page,
          pageSize: pageSize,
          showSizeChanger: true,
          onChange: (page: number, pageSize: number) => {
            setPage(page);
            setPageSize(pageSize);
            // setExportType(-1);
          },
          pageSizeOptions: tablePageSizeOptions,
          showTotal: (totalRecords, page) =>
            `${page[0]}-${page[1]} of ${totalRecords} items`,
        }}
      />

      {openModel.type === "addDepartment" && (
        <AddDepttModel
          isModalOpen={true}
          setOpenModel={() => setOpenModel({ type: "", param: {} })}
          id={openModel.param?.id}
          setTrigger={setAddUpdateTrigger}
        />
      )}
      {openModel.type === "delete" && (
        <ConfirmationModal
          open={true}
          onNo={() => setOpenModel({ type: "", param: {} })}
          onYes={() => handleDelete(openModel.param?.id)}
          text={`Are you sure you want to delete this row "${openModel.param?.name}" ?`}
        />
      )}
      {openModel.type === "isActive" && (
        <ConfirmationModal
          open={true}
          onNo={() => setOpenModel({ type: "", param: "" })}
          onYes={async () => {
            try {
              const apiData = await OrganisationsService.isActiveDepartment(
                currentClient?.id!,
                openModel.param?.id,
                openModel.param?.itemStatus === LedgerStatus.deactive
                  ? LedgerStatus.active
                  : LedgerStatus.deactive
              );
              if (apiData.result) {
                openNotification(
                  "success",
                  `Department "${openModel?.param?.name}" ${
                    openModel?.param?.itemStatus === LedgerStatus.deactive
                      ? "Active"
                      : "InActive"
                  } sucessfully.`
                );

                setTrigger((x) => !x);
                setOpenModel({ type: "", param: "" });
              } else {
                openNotification("error", apiData?.message);
                console.log("error", apiData?.message);
                setOpenModel({ type: "", param: "" });
              }
            } catch (err) {
              openNotification("error", err);
              console.log("error", err);
              setOpenModel({ type: "", param: "" });
            }
          }}
          text={`Are you sure you want to ${
            openModel?.param?.itemStatus === LedgerStatus.deactive
              ? "Active"
              : "InActive"
          }  this "${openModel?.param?.name}" Department. ?`}
        />
      )}
    </Card>
  );
};

export default Departments;
